import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Loader2, Mail, Phone, MapPin, Trash2, CheckCircle2, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubmissions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSubmissions();
  }, [navigate]);

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/contact-submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }
      if (!res.ok) throw new Error('Failed to load submissions');
      const data = await res.json();
      setSubmissions(data);
    } catch (err) {
      toast.error(err.message || 'Could not load submissions');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/contact-submissions/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'read' }),
      });
      if (!res.ok) throw new Error('Failed to update');
      setSubmissions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: 'read' } : s))
      );
      toast.success('Marked as read');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Delete this submission permanently?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/contact-submissions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const filtered = submissions.filter((s) => {
    if (filter === 'new') return (s.status || 'new') === 'new';
    if (filter === 'read') return s.status === 'read';
    return true;
  });
  const newCount = submissions.filter((s) => (s.status || 'new') === 'new').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="submissions-heading">
              <Inbox className="w-8 h-8" />
              Contact Submissions
              {newCount > 0 && (
                <Badge className="bg-white text-cyan-700 hover:bg-white ml-2">
                  {newCount} new
                </Badge>
              )}
            </h1>
            <p className="text-cyan-100 mt-1">All messages sent through the website contact form</p>
          </div>
          <Link to="/admin/dashboard">
            <Button variant="outline" className="text-white border-white hover:bg-white/10" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Dashboard
            </Button>
          </Link>
        </div>
      </div>

      <div className="container mx-auto p-6">
        <div className="flex gap-2 mb-6">
          {['all', 'new', 'read'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
              data-testid={`filter-${f}-btn`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'all' && ` (${submissions.length})`}
              {f === 'new' && ` (${newCount})`}
            </Button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg">No submissions to show</p>
              <p className="text-sm mt-1">Messages from your website contact form will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="submissions-list">
            {filtered.map((s) => {
              const isNew = (s.status || 'new') === 'new';
              return (
                <Card
                  key={s.id}
                  className={`border-l-4 ${isNew ? 'border-l-cyan-500 bg-cyan-50/30' : 'border-l-gray-200'}`}
                  data-testid={`submission-card-${s.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {s.name}
                          {isNew && <Badge className="bg-cyan-600 hover:bg-cyan-600">New</Badge>}
                        </CardTitle>
                        <p className="text-sm text-gray-500 mt-1">{formatDate(s.submitted_at)}</p>
                      </div>
                      <div className="flex gap-2">
                        {isNew && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsRead(s.id)}
                            data-testid={`mark-read-${s.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Mark read
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteSubmission(s.id)}
                          data-testid={`delete-${s.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid md:grid-cols-3 gap-3 text-sm">
                      <a href={`mailto:${s.email}`} className="flex items-center gap-2 text-cyan-700 hover:underline">
                        <Mail className="w-4 h-4" /> {s.email}
                      </a>
                      <a href={`tel:${s.phone}`} className="flex items-center gap-2 text-cyan-700 hover:underline">
                        <Phone className="w-4 h-4" /> {s.phone}
                      </a>
                      <div className="flex items-center gap-2 text-gray-700">
                        <MapPin className="w-4 h-4 flex-shrink-0" /> {s.address}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded border border-gray-200 whitespace-pre-wrap text-gray-800">
                      {s.message}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <a href={`mailto:${s.email}?subject=Re: Your enquiry&body=Hi ${s.name},%0D%0A%0D%0A`}>
                        <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" data-testid={`reply-${s.id}`}>
                          <Mail className="w-4 h-4 mr-1" /> Reply
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissions;
